import type {
  ApplicationDetailData,
  ApplicationLayerData,
} from "../../types/application";
import type { PaymentLine } from "../../types/tabs";
import styles from "../../styles/applicationDetail.module.css";

/** Angular payment log status cell: keep ACH_Check_Pending, otherwise titlecase. */
export function formatPaymentStatusLabel(status: unknown): string {
  const raw = String(status ?? "").trim();
  if (!raw) return "";
  if (raw === "ACH_Check_Pending") return "ACH_Check_Pending";
  return raw
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function paymentHistoryRowIsPaidLike(status: unknown): boolean {
  const s = String(status ?? "").trim().toLowerCase();
  return (
    s === "completed" ||
    s === "success" ||
    s === "successful" ||
    s === "paid" ||
    s === "succeeded"
  );
}

export function paymentStatusBadgeClass(status: unknown): string {
  const s = String(status ?? "").trim().toLowerCase();
  if (paymentHistoryRowIsPaidLike(status)) return styles.statusComplete;
  if (
    s.includes("fail") ||
    s === "cancelled" ||
    s === "canceled" ||
    s === "declined" ||
    s === "error"
  ) {
    return styles.statusFailed;
  }
  return styles.statusPending;
}

export function isApplicationFeePaymentLogType(paymentType: unknown): boolean {
  const t = String(paymentType ?? "").trim();
  if (!t) return false;
  if (t === "APPLICATION_FEE") return true;
  if (t === "APPLICATION_FEE_REFUND") return true;
  if (t === "APPLICATION_FEE_INVOICE") return true;
  return t.startsWith("APPLICATION_FEE");
}

export function coresidentsFromApplicationAll(
  all: ApplicationDetailData | null | undefined,
): ApplicationLayerData[] {
  const raw = all?.co_resident_application;
  if (!raw || typeof raw !== "object") return [];
  return Object.values(raw);
}

export function getPrimaryCoresident(
  list: ApplicationLayerData[],
): ApplicationLayerData | null {
  return (
    list.find(
      (c) =>
        c?.is_primary === true ||
        c?.is_primary === "1" ||
        c?.is_primary === 1,
    ) ?? null
  );
}

/**
 * Angular Signature/Payment Payment Log row visibility (`d-none` parity).
 * Only APPLICATION_FEE / APPLICATION_FEE_REFUND / APPLICATION_FEE_INVOICE rows are
 * scoped to the routed applicant via fk_applicant_id; all other types stay visible
 * for every co-resident view.
 */
export function paymentLogRowForRoutedApplicant(
  payment: PaymentLine,
  viewerApplicantId: string,
  _primaryApplicantId?: string | null,
  _multiResident?: boolean,
): boolean {
  const viewer = String(viewerApplicantId ?? "").trim();
  if (!viewer) return true;

  if (!isApplicationFeePaymentLogType(payment.payment_type)) {
    return true;
  }

  const fkRaw = payment.fk_applicant_id ?? payment.applicant_id;
  const fkStr =
    fkRaw !== undefined && fkRaw !== null && String(fkRaw).trim() !== ""
      ? String(fkRaw).trim()
      : "";

  return fkStr === viewer;
}

export { NAME_OVERRIDE_REASON_OPTIONS } from '../../constants';

export function isPaymentNameOverrideSectionVisible(
  data: ApplicationLayerData | null | undefined,
): boolean {
  const v = data?.payment_name_override;
  if (v === null || v === undefined) return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return v.trim() !== "";
  return Boolean(v);
}

export function normalizePaymentNameOverrideChoice(v: unknown): "YES" | "NO" {
  const s = String(v ?? "").trim().toUpperCase();
  if (s === "YES" || s === "1" || s === "TRUE") return "YES";
  return "NO";
}

function paymentHistoryUnified(
  all: ApplicationDetailData | null | undefined,
  applicantLayer: ApplicationLayerData | null | undefined,
): PaymentLine[] {
  const h = all?.payment_history ?? applicantLayer?.payment_history;
  return Array.isArray(h) ? (h as PaymentLine[]) : [];
}

export function computeIsApplicationPaidForBalanceDue(
  applicationAllData: ApplicationDetailData | null | undefined,
  applicationData: ApplicationLayerData | null | undefined,
  applicantId: string,
): boolean {
  const idStr = String(applicantId ?? "").trim();
  const appRowId = String(
    applicationData?.applicant_id ?? applicationData?.fk_applicant_id ?? "",
  ).trim();
  const history = paymentHistoryUnified(applicationAllData, applicationData);

  let totalApplicationPayment = 0;
  for (const paymentDetail of history) {
    const ptype = paymentDetail?.payment_type;
    if (!ptype) continue;
    const ptUpper = String(ptype).toUpperCase();
    if (
      !ptUpper.includes("APPLICATION_FEE") &&
      !(ptUpper.includes("APPLICATION") && ptUpper.includes("FEE"))
    ) {
      continue;
    }
    if (!paymentHistoryRowIsPaidLike(paymentDetail?.payment_status)) continue;
    const fk = String(
      paymentDetail?.fk_applicant_id ?? paymentDetail?.applicant_id ?? "",
    ).trim();
    if (fk) {
      if (fk !== idStr && (!appRowId || fk !== appRowId)) continue;
    }
    totalApplicationPayment +=
      parseFloat(String(paymentDetail?.payment_amount ?? "0")) || 0;
  }

  if (totalApplicationPayment > 0) {
    return true;
  }

  const adjustments = applicationAllData?.payment_and_payment_adjustment;
  if (Array.isArray(adjustments)) {
    const paidAppLike = adjustments.some((p) => {
      if (!paymentHistoryRowIsPaidLike(p?.payment_status)) return false;
      const t = String(p?.payment_type ?? "").toUpperCase();
      return (
        t.includes("APPLICATION_FEE") ||
        (t.includes("APPLICATION") && t.includes("FEE")) ||
        t.includes("APPLICATION")
      );
    });
    if (paidAppLike) return true;
    if (adjustments.some((p) => paymentHistoryRowIsPaidLike(p?.payment_status))) {
      return true;
    }
  }

  return false;
}

function paymentTypeIsApplicationFeeLike(paymentType: unknown): boolean {
  const t = String(paymentType ?? "").toUpperCase();
  if (!t) return false;
  return (
    t.includes("APPLICATION_FEE") ||
    (t.includes("APPLICATION") && t.includes("FEE"))
  );
}

/**
 * Application fee paid by this applicant only.
 * Unlike `computeIsApplicationPaidForBalanceDue`, a co-applicant's payment never counts:
 * rows carrying another `fk_applicant_id` are skipped instead of marking the whole
 * application paid. Rows with no applicant id are treated as application-wide.
 */
export function computeIsApplicationFeePaidForApplicant(
  applicationAllData: ApplicationDetailData | null | undefined,
  applicationData: ApplicationLayerData | null | undefined,
  applicantId: string,
): boolean {
  const idStr = String(applicantId ?? "").trim();
  const appRowId = String(
    applicationData?.applicant_id ?? applicationData?.fk_applicant_id ?? "",
  ).trim();

  const adjustments = applicationAllData?.payment_and_payment_adjustment;
  const rows: PaymentLine[] = [
    ...paymentHistoryUnified(applicationAllData, applicationData),
    ...(Array.isArray(adjustments) ? (adjustments as PaymentLine[]) : []),
  ];

  return rows.some((row) => {
    if (!paymentTypeIsApplicationFeeLike(row?.payment_type)) return false;
    if (!paymentHistoryRowIsPaidLike(row?.payment_status)) return false;

    const fk = String(
      row?.fk_applicant_id ?? row?.applicant_id ?? "",
    ).trim();
    if (!fk) return true;

    return fk === idStr || (Boolean(appRowId) && fk === appRowId);
  });
}

export function applicantStatusIsApproved(status: unknown): boolean {
  return String(status ?? "").trim().toUpperCase() === "APPROVED";
}

/** Angular application-details: Access Fee Paid + Balance Due block. */
export function shouldShowAccessFeeBalanceDueSection(
  applicationAllData: ApplicationDetailData | null | undefined,
  applicationData: ApplicationLayerData | null | undefined,
  isApplicationPaid: boolean,
): boolean {
  return (
    Boolean(applicationAllData) &&
    String(applicationAllData?.application_status ?? "").toUpperCase() !== "CANCELLED" &&
    applicantStatusIsApproved(applicationData?.applicant_status) &&
    isApplicationPaid
  );
}

/** Angular application-details: Balance Owed $35 block for unpaid application fee. */
export function shouldShowBalanceOwedSection(
  applicationAllData: ApplicationDetailData | null | undefined,
  applicationData: ApplicationLayerData | null | undefined,
  isApplicationPaid: boolean,
): boolean {
  return (
    String(applicationAllData?.application_status ?? "").toUpperCase() !== "CANCELLED" &&
    !applicantStatusIsApproved(applicationData?.applicant_status) &&
    ((applicationData?.applicant_status === "INCOMPLETE" && !isApplicationPaid) ||
      Boolean(applicationData?.application_fee_invoice)) &&
    applicationData?.is_approved_require === "1"
  );
}

export function resolvePaymentLogSourceRows(
  applicationAllData: ApplicationDetailData | null | undefined,
): PaymentLine[] {
  const adjustments = applicationAllData?.payment_and_payment_adjustment;
  if (Array.isArray(adjustments) && adjustments.length > 0) {
    return adjustments as PaymentLine[];
  }
  const history = applicationAllData?.payment_history;
  if (Array.isArray(history) && history.length > 0) {
    return history as PaymentLine[];
  }
  return Array.isArray(adjustments) ? (adjustments as PaymentLine[]) : [];
}

export function shouldShowPaymentLogSection(
  applicationAllData: ApplicationDetailData | null | undefined,
  applicantId: string,
): boolean {
  if (!applicationAllData) return false;
  if (Array.isArray(applicationAllData.payment_and_payment_adjustment)) return true;
  if (Array.isArray(applicationAllData.payment_history) && applicationAllData.payment_history.length > 0) {
    return true;
  }
  return filterPaymentLogRowsForApplicant(applicationAllData, applicantId).length > 0;
}

export function filterPaymentLogRowsForApplicant(
  applicationAllData: ApplicationDetailData | null | undefined,
  applicantId: string,
): PaymentLine[] {
  const allRows = resolvePaymentLogSourceRows(applicationAllData);
  const viewer = String(applicantId ?? "").trim();
  if (!viewer) return allRows;
  return allRows.filter((p) => paymentLogRowForRoutedApplicant(p, viewer));
}

/** Angular hardcodes Balance Owed as $35 for the unpaid application-fee block. */
export function computeBalanceOwedAmount(): string {
  return "$35.00";
}

export function parsePaymentFailedList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((el: string) => {
    const parts = String(el).split("<br/>");
    return parts[0] ? parts[0].trim() : String(el);
  });
}
